import moment from 'moment';
import { extendMoment } from 'moment-range';
import min from 'lodash/min';
import max from 'lodash/max';
import range from 'lodash/range';

import { JOB_STATUS } from '@iso/enums';

export const extendedMoment = extendMoment(moment);

export const dateSort = (startDate, endDate, multiplier) => {
  return moment(startDate).diff(moment(endDate)) * multiplier;
};

export const timeAgo = (date) => {
  if (!date) {
    return null;
  }
  return moment(date).fromNow();
};

export const stringToDate = (dateString, format = 'YYYY-MM-DD') => {
  return dateString ? moment(dateString, format) : '';
};

export const stringToTime = (timeString, format = 'hh:mm A') => {
  return timeString ? moment(timeString, format) : '';
};

export const formatDateString = (dateString, format = 'YYYY-MM-DD') => {
  return dateString ? moment(dateString).format(format) : '';
};

export const formatDateRange = (startDate, endDate) => {
  if (!moment(startDate, moment.ISO_8601, true).isValid() || !moment(endDate, moment.ISO_8601, true).isValid()) {
    return null;
  }

  const sDate = moment(startDate);
  const eDate = moment(endDate);
  // if (!sDate.isValid() || !eDate.isValid()) {
  //   return null;
  // }

  const sYear = sDate.format('YYYY');
  const sMonth = sDate.format('MMM');
  const sDay = sDate.format('Do');

  const eYear = eDate.format('YYYY');
  const eMonth = eDate.format('MMM');
  const eDay = eDate.format('Do');

  // Check if month and year are the same
  if (sYear === eYear && sMonth === eMonth) {
    return `${sMonth} ${sDay} - ${eMonth} ${eDay}, ${eYear}`;
  }

  // Check if year is the same
  if (sYear === eYear) {
    return `${sMonth} ${sDay} - ${eMonth} ${eDay}, ${eYear}`;
  }

  return `${sMonth} ${sDay}, ${sYear} - ${eMonth} ${eDay}, ${eYear}`;
};

export const formatTimeString = (timeString, format = 'hh:mm A') => {
  return timeString ? moment(timeString, 'HH:mm:ss').format(format) : '';
};

export const isSequenceDates = (dates) => {
  const minDate = min(dates);
  const maxDate = max(dates);
  const diffDays = moment(maxDate).diff(moment(minDate), 'days') + 1;

  return dates.length === diffDays ? true : false;
};

export const isAfter = (start, end) => {
  if (!end) {
    return false;
  }
  if (!start) {
    return moment().isAfter(end);
  }
  return moment(start).isAfter(end);
};

export const isBefore = (start, end) => {
  if (!end) {
    return false;
  }
  if (!start) {
    return moment().isBefore(end);
  }
  return moment(start).isBefore(end);
};

export const isValidDate = (...dateArgs) => {
  return moment(...dateArgs).isValid();
};

export const getYear = (date) => {
  const _moment = date ? moment(date) : moment();
  return _moment.year();
};

export const getMonth = (date) => {
  const _moment = date ? moment(date) : moment();
  return _moment.month();
};

export const getDate = (date) => {
  const _moment = date ? moment(date) : moment();
  return _moment.date();
};

export const addGoogleCalendarEvent = ({ startDate, wrapDate, title }) => {
  const jobStartDate = startDate?.replace(/-/g, '');
  const jobEndDate = moment(wrapDate, 'YYYY-MM-DD')
    .add('days', 1)
    .format('YYYYMMDD');
  const url = `https://www.google.com/calendar/event?action=TEMPLATE&sf=true&output=xml&dates=${jobStartDate}/${jobEndDate}&text=${title}`;
  window.open(url, '_blank');
};

export const isVisibleAddToCalendarButton = (job) => {
  return (
    moment().isSameOrBefore(job.wrapDate, 'day') &&
    job.status !== JOB_STATUS.WRAPPED
  );
};

/**
 *  * Return array years started from startDate with step
 *
 * @example getYearsRange(2013, 2);
 * @return [2014, 2016, 2018, 2020, 2022];
 *
 * @param  {number} startDate default value = 1939
 * @param  {number} step default value = 1
 *
 * @return {Array<number>}
 */

export const getYearsRange = (startDate = 1939, step = 1) => {
  const currentYear = new Date().getFullYear();
  return range(currentYear, startDate, -step);
};

export function timeDifference(givenTime) {
  givenTime = new Date(givenTime);
  const milliseconds = new Date().getTime() - givenTime.getTime();
  const numberEnding = (number) => {
    return number > 1 ? 's' : '';
  };
  const number = (num) => (num > 9 ? '' + num : '0' + num);
  const getTime = () => {
    let temp = Math.floor(milliseconds / 1000);
    const years = Math.floor(temp / 31536000);
    if (years) {
      const month = number(givenTime.getUTCMonth() + 1);
      const day = number(givenTime.getUTCDate());
      const year = givenTime.getUTCFullYear() % 100;
      return `${day}-${month}-${year}`;
    }
    const days = Math.floor((temp %= 31536000) / 86400);
    if (days) {
      if (days < 28) {
        return days + ' day' + numberEnding(days);
      } else {
        const months = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ];
        const month = months[givenTime.getUTCMonth()];
        const day = number(givenTime.getUTCDate());
        return `${day} ${month}`;
      }
    }
    const hours = Math.floor((temp %= 86400) / 3600);
    if (hours) {
      return `${hours} hour${numberEnding(hours)} ago`;
    }
    const minutes = Math.floor((temp %= 3600) / 60);
    if (minutes) {
      return `${minutes} minute${numberEnding(minutes)} ago`;
    }
    return 'a few seconds ago';
  };
  return getTime();
}

/**
 * Return difference between two dates in years
 *
 * @param  {Object} config
 * @param  {
 *  startDate = moment(),
 *  startDateFormat = string,
 *  endDate = moment(),
 *  endDateFormat = string,
 * }
 *
 * @return {null || number}
 */
export const getDateDiffInYears = (
  { startDate, startDateFormat, endDate, endDateFormat } = {}
) => {
  if (!endDate) return null;

  const start = startDate
    ? (startDateFormat
        ? moment(startDate, startDateFormat, true)
        : moment(startDate))
    : moment(); // fallback to now

  const end = endDateFormat
    ? moment(endDate, endDateFormat, true)
    : moment(endDate);

  if (!start.isValid() || !end.isValid()) return null;

  return moment.duration(start.diff(end)).asYears();
};

/**
 * @param  {Date UTC} utcDate
 *
 * @return  {String}
 */
export const getCommentDate = (utcDate) => {
  const date = moment.utc(utcDate).local();
  const formattedDate = date.format('MMM D, YYYY');
  const formattedTime = date.format('h:mm a');
  return `${formattedDate} at ${formattedTime}`;
};
